import ProfileMenuAgency from '@/components/ProfileMenu/ProfileMenuAgency.tsx';
import { menuTabs } from '@/components/sidebar/sidebarTabsAgency.tsx';
import { PlusOutlined } from '@ant-design/icons';
import { Button, Spin, Tooltip } from 'antd';
import { AgencyBalanceBean, NewPaymentBean } from 'common/api/api.ts';
import { agencyBalanceApi } from 'common/api/entitiesApi.tsx';
import { AddFundsModal } from 'common/components/AddFundsModal.tsx';
import { useAppContext } from 'common/context/AppContext.tsx';
import { CommonRoutes, getBaseRoute, getRouteActionTitle, getRouteTitleAlias, normalizeRoutePath } from 'common/context/commonRoutes.ts';
import { useUserContext } from 'common/context/UserContext.tsx';
import { formatBalance, showError, showMessage } from 'common/util/Util.tsx';
import * as React from 'react';
import { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import './style.css';
import PaySystemEnum = NewPaymentBean.PaySystemEnum

interface BreadcrumbProps {
  collapsed: boolean
  balance?: AgencyBalanceBean
  onBalanceRefresh?: () => void
  stripePaymentsEnabled: boolean
}

export const BreadcrumbAgency: React.FC<BreadcrumbProps> = ({ collapsed, balance, onBalanceRefresh, stripePaymentsEnabled }) => {
  const [breadcrumb, setBreadcrumb] = useState<React.ReactNode[]>([])
  const [addFundsModalOpen, setAddFundsModalOpen] = useState(false)
  const [paymentLoading, setPaymentLoading] = useState(false)
  const location = useLocation()
  const { user, blackTheme } = useUserContext()
  const appContext = useAppContext()
  const { preloader, contextHolder } = appContext
  const navigateTo = useNavigate()

  const closeAddFundsModal = () => {
    setAddFundsModalOpen(false)
  }

  const menuTitleByKey = React.useMemo(() => {
    const map = new Map<string, string>()
    const traverse = (items: any[]) => {
      items.forEach((item) => {
        if (item?.url && item?.label) {
          map.set(normalizeRoutePath(item.url), item.label)
        }
        if (item.children?.length) {
          traverse(item.children)
        }
      })
    }
    traverse(menuTabs as any[])
    return map
  }, [])

  const getItemName = React.useCallback(
    (pathname: string) => {
      return getRouteTitleAlias(pathname) || menuTitleByKey.get(pathname) || 'Dashboard'
    },
    [menuTitleByKey],
  )

  const buildBreadcrumb = React.useCallback(() => {
    const pathParts = location.pathname.split('/').filter(Boolean)
    const sectionRoute = getBaseRoute(location.pathname)

    const crumbs: React.ReactNode[] = []

    // Home
    crumbs.push(
      <Link key="home" className="breadcrumbBar__item" to="/">
        Home
      </Link>,
    )

    if (pathParts.length > 0) {
      const sectionName = getItemName(sectionRoute)
      crumbs.push(<span className="breadcrumbBar__arrow" key="arrow1" />)
      crumbs.push(
        <Link key="section" className="breadcrumbBar__item" to={sectionRoute === CommonRoutes.PROFILE ? location.pathname : sectionRoute}>
          {sectionName}
        </Link>,
      )
    }

    // Actions: create / edit / copy
    if (pathParts.some((p) => ['create', 'edit', 'copy'].includes(p))) {
      const action = pathParts.find((p) => ['create', 'edit', 'copy'].includes(p))
      if (action) {
        const actionLabel = action === 'create' ? 'New' : action === 'edit' ? 'Edit' : action === 'copy' ? 'Copy' : ''
        const entityName = getRouteActionTitle(sectionRoute, getItemName(sectionRoute))
        crumbs.push(<span className="breadcrumbBar__arrow" key="arrow2" />)
        crumbs.push(
          <span key="action" className="breadcrumbBar__item">
            {actionLabel} {entityName}
          </span>,
        )
      }
    }

    return crumbs
  }, [location.pathname, getItemName])

  useEffect(() => {
    setBreadcrumb(buildBreadcrumb())
  }, [buildBreadcrumb])

  const startPayment = (amount: number) => {
    if (!user?.agencyId) {
      return
    }

    const paymentWindow = window.open('', '_blank')
    if (!paymentWindow) {
      showMessage(appContext.messageApi, 'Error', 'Allow popups to continue to Stripe.')
      return
    }
    setPaymentLoading(true)
    agencyBalanceApi
      .startAgencyPayment({
        agencyId: user.agencyId,
        amount,
        paySystem: PaySystemEnum.STRIPE,
      })
      .then((res) => {
        const paymentUrl = res.data
        if (!paymentUrl) throw new Error('Payment URL is missing')
        paymentWindow.location.href = paymentUrl
        const checkClosedInterval = window.setInterval(() => {
          if (paymentWindow.closed) {
            window.clearInterval(checkClosedInterval)
            onBalanceRefresh?.()
          }
        }, 1000)
        closeAddFundsModal()
        navigateTo(CommonRoutes.DASHBOARD)
      })
      .catch((e) => {
        paymentWindow.close()
        showError(appContext, e)
      })
      .finally(() => setPaymentLoading(false))
  }

  const addFundsModal = (
    <AddFundsModal
      open={addFundsModalOpen}
      loading={paymentLoading}
      onCancel={closeAddFundsModal}
      onSubmit={startPayment}
    />
  )

  return (
    <div
      className={`breadcrumbBar ${blackTheme ? 'breadcrumb_black' : 'breadcrumb_white'}`}
      style={{
        width: `calc(100% - ${collapsed ? 80 : 214}px)`,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <div
          className={`breadcrumbBar__path ${blackTheme ? 'breadcrumbBar__path_black' : 'breadcrumbBar__path_white'}`}
          style={{ display: 'inline-block' }}
        >
          {breadcrumb}
        </div>
        {preloader && (
          <div
            style={{
              marginLeft: '10px',
              minWidth: '120px',
              minHeight: '32px',
              display: 'flex',
              alignItems: 'center',
              flexShrink: 0,
            }}
          >
            <Spin tip="Loading" size="large" />
          </div>
        )}
      </div>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: 60,
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: '36px',
            minWidth: '146px',
            backgroundColor: '#FFFFFF1F',
            borderRadius: '4px',
            color: '#FFFFFF',
          }}
        >
          {!balance ? (
            <Spin />
          ) : (
            <div
              style={{
                color: '#FFFFFF',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '36px',
              }}
            >
              Balance:&nbsp;<span style={{ fontWeight: 600 }}>{'$' + formatBalance(balance.runtimeAvailable)}</span>
            </div>
          )}
        </div>
        <Tooltip title={stripePaymentsEnabled ? undefined : 'Stripe payments are currently unavailable'}>
        <span><Button
          icon={<PlusOutlined/>}
          style={{
            marginLeft: '10px',
            color: '#FFFFFF',
            backgroundColor: '#FFFFFF1F',
            height: '36px',
          }}
          type="text"
          disabled={!stripePaymentsEnabled}
          onClick={() => setAddFundsModalOpen(true)}
        >
          Add Funds
        </Button></span></Tooltip>
        <ProfileMenuAgency />
      </div>
      {addFundsModal}
      {contextHolder}
    </div>
  )
}
